














































































































import { Component, Vue, Prop, Ref, ProvideReactive } from 'vue-property-decorator';
import { SmartQuery } from 'vue-apollo-decorator';
import { ALL_PAYCHECK_PERIODS_FOR_PAYCHECK_LIST } from '@/queries/PaycheckPeriod';
import { GQL_PaycheckCreate, GQL_PaycheckLineCreate } from '@/queries/global';
import { GQL_createPaycheck, GQL_createPaycheckVariables } from '@/queries/__generated__/createPaycheck';
import { GQL_paycheckNew, GQL_paycheckNewVariables, GQL_paycheckNew_newPaycheck, GQL_paycheckNew_newPaycheck_paycheckLines } from '@/queries/__generated__/paycheckNew';
import { GQL_currentUnitPaycheckPeriodsForPaycheckList } from '@/queries/__generated__/currentUnitPaycheckPeriodsForPaycheckList';
import PaycheckForm from '@/components/Paycheck/PaycheckForm.vue';
import { InMemoryCache } from 'apollo-cache-inmemory';
import PaycheckTaxSummary from '@/components/Paycheck/PaycheckTaxSummary.vue';
import { UnsavedChanges } from '@/decorators/unsaved-changes';
import i18n from '@/i18n';
import permissionGuard from '@/guards/permission-guard';
import unitPaycheckSettingsRequiredGuard from '@/guards/unit-paycheck-settings-required-guard';
import { IErrors, hasCode } from '@/utils/graphql-errors';
import PermissionService from '@/utils/permission-service';
import PaycheckSettingsService from '@/utils/paycheck-settings-service';
import { GQL_workRelationPaycheckSums_workRelation_paycheckSums } from '@/queries/__generated__/workRelationPaycheckSums';
import PaycheckSumsFetcher from '@/components/Paycheck/PaycheckSumsFetcher.vue';
import PaycheckTimestamps from '@/components/Paycheck/PaycheckTimestamps.vue';
import gql from 'graphql-tag';
import { paycheckNewFragments } from '@/queries/paycheckNewFragments';
import { taxCalculator, TaxDeductionCalculationResult } from '@/app/Skatteetaten/tax-deduction';
import Decimal from '@/utils/decimal';
import { lineIsTaxDeductionOrdinary, moveExcessTaxDeductiontoTaxDeductionVolunary, PaycheckFormLine } from '@/components/Paycheck/PaycheckForm';
import { WithUID } from '@/utils/utils';
import formatAmount from '@/filters/formatAmount';

const PAYCHECK_NEW = gql`
  query paycheckNew($workRelationId: ID!, $periodId: ID!) {
    newPaycheck(workRelationId: $workRelationId, periodId: $periodId) {
      ...paycheckNewFragments,
    }
  }
  ${paycheckNewFragments}
`;

const CREATE_PAYCHECK = gql`
  mutation createPaycheck($paycheck: PaycheckCreate!) {
    createPaycheck(input: { paycheck: $paycheck }) {
      paycheck {
        id
      }
    }
  }
`;

type TaxDeductionSaveWarning = {
  isWarningPopupOpen: boolean;
  calculatedTaxDeduction: Decimal | null;
  taxDeductionCalculationError: string | null;
  excessTaxDeductionOrdinary: Decimal | null;
  moveAndSaveLoading: boolean;
  saveAsIsLoading: boolean;
}

@Component({
  components: {
    PaycheckForm,
    PaycheckTaxSummary,
    PaycheckSumsFetcher,
    PaycheckTimestamps,
  },
  beforeRouteEnter(to, from, next) {
    permissionGuard('canCreatePaychecks', to, from, next)
      .then(() => {
        unitPaycheckSettingsRequiredGuard(to, from, next);
      }).catch(router => {
        router.push({ name: 'Dashboard' });
        router.app.$sMessage.error(router.app.$t('noPermissions'));
      });
  },
})
export default class PaychecksNew extends Vue {
  @Prop({ type: String, required: true }) private workRelationId!: string;
  @Prop({ type: String, required: true }) private subUnitId!: string;
  @Prop({ type: String, required: true }) private periodId!: string;

  @Prop({ type: Boolean, required: true })
  private advancedPaycheckMode!: boolean;

  @SmartQuery<PaychecksNew, GQL_paycheckNew, GQL_paycheckNewVariables>({
    query: PAYCHECK_NEW,
    fetchPolicy: 'no-cache',
    variables() { return { workRelationId: this.workRelationId, periodId: this.periodId } },
    update(data) {
      let fields = {};
      if (this.advancedPaycheckMode) {
        fields = {
          ...fields,
          advancedMode: this.advancedPaycheckMode,
          // unset since the user has to select
          shouldSendToAltinn: null,
          shouldSendToFakturabank: null,
        };
      }

      return ({
        ...data.newPaycheck,
        periodId: data.newPaycheck.period.id,
        unitId: data.newPaycheck.unit.id,
        currencyCode: data.newPaycheck.currencyCode,
        workRelationId: data.newPaycheck.workRelation.id,
        companyMunicipalityId: data.newPaycheck.companyMunicipality.id,
        ...fields,
        paycheckLines: data.newPaycheck.paycheckLines.map(line => ({
          ...line,
          paycheckLineTemplateId: line.paycheckLineTemplate.id,
          carId: line.car?.id || '',
          projectId: line.project?.id || '',
          departmentId: line.department?.id || '',
        } as GQL_paycheckNew_newPaycheck_paycheckLines & GQL_PaycheckLineCreate)),
      } as GQL_paycheckNew_newPaycheck & GQL_PaycheckCreate);
    },
    error(error: IErrors) {
      if (hasCode(error, 'NO_DRAFT')) {
        this.$sMessage.error(this.$t('Paycheck.noDraft'));
        this.$router.push({ name: 'Paychecks' });
      }
    },
  }) private paycheck!: GQL_paycheckNew_newPaycheck & GQL_PaycheckCreate;

  private get notDeletedLines(): WithUID<PaycheckFormLine>[] {
    return (this.paycheck.paycheckLines as WithUID<PaycheckFormLine>[])
      .filter((line) => !(line as unknown as {_destroy: boolean})._destroy);
  }

  private validForm = true;
  @UnsavedChanges<PaychecksNew>({
    messages: {
      entity: i18n.t('Paycheck.paycheck'),
    },
    saveAndContinue() {
      return this.saveAndContinue();
    },
  })
  private dirtyForm = false;
  private CREATE_PAYCHECK = CREATE_PAYCHECK;
  private PermissionService = PermissionService;

  private formatAmount = formatAmount;

  @Ref() private readonly paycheckForm?: PaycheckForm;

  private get isLocked(): boolean {
    return !!this.paycheck.lockedAt;
  }

  private i18nMoveVoluntaryTaxToVoluntaryLine() {
    return this.$t(
      "Paycheck.moveVoluntaryTaxToVoluntaryLine",
      { amount: formatAmount(this.taxDeductionSaveWarning?.excessTaxDeductionOrdinary?.times(-1) ?? null) },
    );
  }
  private i18nSavePaycheckAsIs() {
    return this.$t("Paycheck.TaxDeductionSaveWarning.savePaycheckAsIs");
  }

  private formatForMutation(paycheck: GQL_paycheckNew_newPaycheck & GQL_PaycheckCreate): GQL_PaycheckCreate {
    return {
      workRelationId: paycheck.workRelationId,
      periodId: paycheck.periodId,
      currencyCode: paycheck.currencyCode,
      startDate: paycheck.startDate,
      endDate: paycheck.endDate,
      dueDate: paycheck.dueDate,
      companyMunicipalityId: paycheck.companyMunicipalityId,
      employeeBban: paycheck.employeeBban,
      employeeIban: paycheck.employeeIban,
      employeeSwift: paycheck.employeeSwift,
      paidDate: paycheck.paidDate,
      comment: paycheck.comment,
      internalComment: paycheck.internalComment,
      journalDate: paycheck.journalDate,
      taxDeductionMethod: paycheck.taxDeductionMethod,
      unitId: paycheck.unitId,
      number: paycheck.number,
      advancedMode: paycheck.advancedMode,
      shouldSendToAltinn: paycheck.shouldSendToAltinn,
      shouldSendToFakturabank: paycheck.shouldSendToFakturabank,
      paycheckLines: (paycheck.paycheckLines as Array<GQL_paycheckNew_newPaycheck_paycheckLines & GQL_PaycheckLineCreate>).map(line => ({
        paycheckLineTemplateId: line.paycheckLineTemplateId,
        quantity: line.quantity,
        rate: line.rate,
        sum: line.sum,
        carId: line.carId,
        projectId: line.projectId,
        departmentId: line.departmentId,
        altinnAdditional: line.altinnAdditional,
      } as GQL_PaycheckLineCreate)),
      prepayments: paycheck.prepayments.map(prepayment => ({
        amount: prepayment.amount,
        paymentDate: prepayment.paymentDate,
      })),
    } as GQL_PaycheckCreate;
  }

  private saveAndContinue(): Promise<boolean> {
    return this.savePaycheck()
      .then(() => { return true })
      .catch(() => { return false });
  }

  public async calculateTaxDeduction(): Promise<TaxDeductionCalculationResult> {
    const calculator = taxCalculator(this.paycheck, this.paycheckSums!);
    if(!this.paycheck.taxDeductionMethod) {
      return calculator.emptyTaxDeductionCalculation();
    }

    return await calculator.calculateTaxDeduction(this.paycheck.taxDeductionMethod);
  }

  private taxDeductionSaveWarning: TaxDeductionSaveWarning = {
    isWarningPopupOpen: false,
    calculatedTaxDeduction: null,
    taxDeductionCalculationError: null,
    excessTaxDeductionOrdinary: null,
    moveAndSaveLoading: false,
    saveAsIsLoading: false,
  }
  private savePaycheckLoading = false;
  private async checkTaxDeductionAndSave() {
    const taxDeductionWarning = await this.calculateTaxDeduction();

    if (taxDeductionWarning.kind === `calculation`) {
      const ordinaryTaxDeduction: Decimal = this.notDeletedLines
        .filter(lineIsTaxDeductionOrdinary)
        .reduce(((acc, line) => acc.plus(line.sum)), new Decimal(0));
      const expectedTaxDeduction: Decimal = taxDeductionWarning.taxDeduction.times(-1);

      if (ordinaryTaxDeduction.lessThan(expectedTaxDeduction)) {
        this.taxDeductionSaveWarning.excessTaxDeductionOrdinary =
          ordinaryTaxDeduction.minus(expectedTaxDeduction);
        this.taxDeductionSaveWarning.isWarningPopupOpen = true;
        return;
      }
    }

    this.taxDeductionSaveWarning.excessTaxDeductionOrdinary = null;
    this.savePaycheckLoading = true;
    return this.savePaycheckAndMoveToEdit()
      .finally(() => { this.savePaycheckLoading = false });
  }

  private savePaycheckAndMoveToEdit() {
    return this
      .savePaycheck()
      .then((data) => {
        this.resetFormDirty();
        this.$router.replace({
          name: this.advancedPaycheckMode ? 'AdvancedPaychecksEdit' : 'PaychecksEdit',
          params: { id: data.data!.createPaycheck!.paycheck!.id },
        });
      });
  }

  private savePaycheck() {
    return this.$apollo
      .mutate<GQL_createPaycheck, GQL_createPaycheckVariables>({
        mutation: CREATE_PAYCHECK,
        /* eslint-disable @typescript-eslint/no-explicit-any */
        update: this.addToCache as any,
        variables: { paycheck: this.formatForMutation(this.paycheck) },
      })
      .then((x) => {
        this.$sMessage.success(this.$t('Message.saveSuccess'));
        PaycheckSettingsService.expandSection(false, this.periodId, this.subUnitId, this.workRelationId);
        return x;
      });
  }

  private moveExcessTaxDeductiontoTaxDeductionVolunaryAndSave() {
    const taxDeductionToMove = this.taxDeductionSaveWarning.excessTaxDeductionOrdinary;
    if (!taxDeductionToMove) return;

    this.taxDeductionSaveWarning.moveAndSaveLoading = true;
    moveExcessTaxDeductiontoTaxDeductionVolunary(this.paycheck, taxDeductionToMove)
      .then(() => { return this.savePaycheckAndMoveToEdit() })
      .finally(() => {
        this.taxDeductionSaveWarning.moveAndSaveLoading = false;
        this.taxDeductionSaveWarning.isWarningPopupOpen = false;
      });
  }

  private savePaycheckAsIs() {
    this.taxDeductionSaveWarning.saveAsIsLoading = true;
    this.savePaycheckAndMoveToEdit()
      .finally(() => {
        this.taxDeductionSaveWarning.saveAsIsLoading = false;
        this.taxDeductionSaveWarning.isWarningPopupOpen = false;
      });
  }

  private addToCache(store: InMemoryCache) {
    // can crash if query is not in cache. Happens when you open in new tab
    const data = (store.readQuery({ query: ALL_PAYCHECK_PERIODS_FOR_PAYCHECK_LIST })) as GQL_currentUnitPaycheckPeriodsForPaycheckList;

    if (!data.currentUnit || !data.currentUnit.paycheckPeriods) return;

    const myPeriod = data.currentUnit.paycheckPeriods
      .find(period => period.id === this.periodId);

    if (!myPeriod) return;

    myPeriod.paycheckCount += 1;
    store.writeQuery({ query: ALL_PAYCHECK_PERIODS_FOR_PAYCHECK_LIST, data });
  }

  private resetFormDirty(): void {
    this.paycheckForm && this.paycheckForm.resetFormDirty();
  }

  private get paycheckIsOneOrMoreMonthsInAdvance(): boolean {
    return this.$dayjs(this.paycheck.period.bookkeepingPeriod, `YYYY-MM`, true)
      .diff(this.$dayjs().format('YYYY-MM'), 'month') >= 1;
  }

  private get paycheckIsMoreThanTwoMonthsInPast(): boolean {
    return this.$dayjs().diff(this.paycheck.period.bookkeepingPeriod, 'month') > 2;
  }

  @ProvideReactive()
  private paycheckSums: GQL_workRelationPaycheckSums_workRelation_paycheckSums[] | null = null;

  private setPaycheckSums(paycheckSums: GQL_workRelationPaycheckSums_workRelation_paycheckSums[]) {
    this.paycheckSums = paycheckSums;
  }
}
